/**
 * CSV export helpers.
 *
 * Deliberately dependency-free — the format is a few lines of escaping, and the
 * app doesn't need another package for it.
 */

export interface CsvColumn<T> {
    /** Column header as it appears in the file. */
    header: string;
    /** Pulls the cell value for a row. Return '' for blanks, never null. */
    value: (row: T, index: number) => string | number | null | undefined;
}

/**
 * Spreadsheets treat a leading =, +, -, @ (or tab/CR) as the start of a formula,
 * so a display name like `=HYPERLINK(...)` becomes executable when the file is
 * opened. Every field here is user-supplied — names, emails — so each is
 * neutralised with a leading apostrophe, which Excel and Sheets both strip on
 * display.
 *
 * https://owasp.org/www-community/attacks/CSV_Injection
 */
const neutraliseFormula = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

/** Quotes a field and doubles any embedded quotes, per RFC 4180. */
const escapeField = (raw: string | number | null | undefined): string => {
    if (raw === null || raw === undefined) return '';

    const asString = neutraliseFormula(String(raw));
    // Quote whenever the value could otherwise break the row or column split.
    if (/[",\n\r]/.test(asString)) {
        return `"${asString.replace(/"/g, '""')}"`;
    }
    return asString;
};

/** Builds an RFC 4180 CSV string from rows and a column spec. */
export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
    const header = columns.map((column) => escapeField(column.header)).join(',');
    const body = rows.map((row, index) => columns.map((column) => escapeField(column.value(row, index))).join(','));

    // CRLF: Excel is the most common destination and is happiest with it.
    return [header, ...body].join('\r\n');
}

/** `users-export-2026-08-07.csv` */
export function timestampedFilename(prefix: string): string {
    const now = new Date();
    const stamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
    ].join('-');
    return `${prefix}-${stamp}.csv`;
}

/** Triggers a browser download for the given CSV text. */
export function downloadCsv(filename: string, csv: string): void {
    // Leading BOM so Excel reads the file as UTF-8 — without it, accented
    // characters in names render as mojibake. Written as an escape rather than
    // a literal so the invisible character can't be lost in a copy/paste.
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoking immediately can cancel the download in some browsers; a tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
